import { useEffect } from 'react';
import { useAudienceStore } from '@/stores/audience-store';
import { useAudienceBuilderUiStore } from '@/stores/audience-builder-ui-store';
import { draftFromStore } from '../lib/draft-store-sync';
import { draftContentEquals } from '../lib/draft-reconciler';
import type { DraftPatch } from '../types/draft';
import type { LocalDraftMirror } from './use-local-draft-mirror';

interface UseDraftStoreSubscriberArgs {
  mirror: LocalDraftMirror;
  markDirty: (patch: DraftPatch) => void;
  isHydrating: () => boolean;
  getLastSavedPatch: () => DraftPatch | null;
}

/**
 * Subscribes to audience-store changes and mirrors them (localStorage + autosave).
 *
 * Guards (in order):
 *   1. Not during hydration (synchronous setters fire while hydrating).
 *   2. User actually edited something — hasUserEdited (in builder-ui-store) is
 *      cleared at end of hydration so post-hydration programmatic mutations
 *      don't qualify.
 *   3. Patch content differs from last server-synced patch (prevents no-op
 *      writes that would update the mirror's timestamp for semantically
 *      identical state).
 */
export function useDraftStoreSubscriber({
  mirror,
  markDirty,
  isHydrating,
  getLastSavedPatch,
}: UseDraftStoreSubscriberArgs): void {
  useEffect(() => {
    const unsub = useAudienceStore.subscribe((s) => {
      if (isHydrating()) return;
      if (!useAudienceBuilderUiStore.getState().hasUserEdited) return;
      const patch = draftFromStore(s);
      const lastSaved = getLastSavedPatch();
      if (lastSaved && draftContentEquals(patch, lastSaved)) {
        return;
      }
      mirror.write(patch);
      markDirty(patch);
    });
    return unsub;
  }, [mirror, markDirty, isHydrating, getLastSavedPatch]);
}
